import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import i18next from "i18next";
import Moment from 'react-moment';
import 'moment/locale/fr';
import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';

import httpClient from '../_shared/HttpClient';
import Loader from '../_shared/Loader';
import ScrollBody from '../_shared/ScrollBody';
import TeamAvatar from '../_shared/TeamAvatar';
import { appStore } from '../../appStore.js';

const useStyles = makeStyles(theme => ({
    pageSection: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
    teamHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    teamLogo: {
        width: theme.spacing(10),
        height: theme.spacing(10),
    },
    sectionTitle: {
        marginBottom: theme.spacing(1),
        fontWeight: 600,
    },
    gameScore: {
        fontWeight: 600,
    },
    rankingText: {
        marginTop: theme.spacing(0.5),
    },
    smallAvatar: {
        width: theme.spacing(4),
        height: theme.spacing(4),
    },
    muted: {
        fontStyle: 'italic',
    },
    matchupRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.75),
    },
    versus: {
        fontWeight: 600,
    },
}));

const TeamPage = (props) => {
    const { dispatch } = useContext(appStore);
    const classes = useStyles();
    const { leagueID, teamID } = props.match.params;

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        dispatch({ type: 'updateNavigation', payload: { navTitle: 'Equipe' } });
    }, [dispatch]);

    useEffect(() => {
        props.setLeagueID(leagueID);
        loadData();
    }, [leagueID, teamID]);

    const loadData = async () => {
        try {
            setIsLoading(true);
            const result = await httpClient('get', 'Calendar/GetTeamPage', { leagueID, teamID });
            setData(result);
        }
        catch (err) {
            console.log(err);
        }
        setIsLoading(false);
    };

    const getScoreLabel = (game) => {
        if (!game) return '-';
        return `${game.homeScore} - ${game.guestScore}`;
    };

    const getMatchupLabel = (game) => {
        if (!game) return '';
        return `${game.homeTeam ? game.homeTeam.name : 'Equipe locale'} vs ${game.guestTeam ? game.guestTeam.name : 'Equipe visiteuse'}`;
    };
    if (isLoading) {
        return <Loader isLoading={isLoading}><div></div></Loader>;
    }

    if (!data || !data.team) {
        return (
            <Paper className={classes.pageSection}>
                <Typography>Aucune donnee d&apos;equipe disponible.</Typography>
            </Paper>
        );
    }

    return (
        <>
            <ListItem button dense disableGutters divider component={Link} to={`/home/calendar/${leagueID}`}>
                <ListItemAvatar>
                    <Avatar className={classes.smallAvatar}>
                        <BackIcon />
                    </Avatar>
                </ListItemAvatar>
                <ListItemText
                    primary="Retour au calendrier"
                    secondary={data.rankingSeasonDescription ? `Saison: ${data.rankingSeasonDescription}` : ''}
                />
            </ListItem>

            <ScrollBody hasMorePrevious={false} hasMoreNext={false}>
                <Paper className={classes.pageSection}>
                    <div className={classes.teamHeader}>
                        <TeamAvatar team={data.team} className={classes.teamLogo} />
                        <div>
                            <Typography variant="h4">{data.team.name}</Typography>
                            <Typography variant="body1" className={classes.rankingText}>
                                Position au classement: {data.rankingPosition ? `#${data.rankingPosition}` : 'N/D'}
                            </Typography>
                        </div>
                    </div>
                </Paper>

                <Paper className={classes.pageSection}>
                    <Typography variant="h6" className={classes.sectionTitle}>Saison courante</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="center">Matchs joues</TableCell>
                                    <TableCell align="center">Victoires</TableCell>
                                    <TableCell align="center">Defaites</TableCell>
                                    <TableCell align="center">Nuls</TableCell>
                                    <TableCell align="center">Buts pour</TableCell>
                                    <TableCell align="center">Buts contre</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell align="center">{data.seasonStats ? data.seasonStats.gamesPlayed : 0}</TableCell>
                                    <TableCell align="center">{data.seasonStats ? data.seasonStats.wins : 0}</TableCell>
                                    <TableCell align="center">{data.seasonStats ? data.seasonStats.losses : 0}</TableCell>
                                    <TableCell align="center">{data.seasonStats ? data.seasonStats.ties : 0}</TableCell>
                                    <TableCell align="center">{data.seasonStats ? data.seasonStats.goalsFor : 0}</TableCell>
                                    <TableCell align="center">{data.seasonStats ? data.seasonStats.goalsAgainst : 0}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Paper className={classes.pageSection}>
                    <Typography variant="h6" className={classes.sectionTitle}>Stats des joueurs</Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Joueur</TableCell>
                                    <TableCell align="center">Matchs</TableCell>
                                    <TableCell align="center">Buts</TableCell>
                                    <TableCell align="center">Assistances</TableCell>
                                    <TableCell align="center">Points</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.playerStats || []).map((row) => (
                                    <TableRow key={row.playerID}>
                                        <TableCell>{`${row.name}${row.defaultNumber ? ' #' + row.defaultNumber : ''}`}</TableCell>
                                        <TableCell align="center">{row.gamesPlayed}</TableCell>
                                        <TableCell align="center">{row.goals}</TableCell>
                                        <TableCell align="center">{row.assists}</TableCell>
                                        <TableCell align="center">{row.points}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Paper className={classes.pageSection}>
                            <Typography variant="h6" className={classes.sectionTitle}>Dernier match</Typography>
                            {
                                data.lastMatch ?
                                    <>
                                        <div className={classes.matchupRow}>
                                            <TeamAvatar team={data.lastMatch.homeTeam} className={classes.smallAvatar} />
                                            <Typography>{data.lastMatch.homeTeam ? data.lastMatch.homeTeam.name : 'Equipe locale'}</Typography>
                                            <Typography className={classes.versus}>vs</Typography>
                                            <TeamAvatar team={data.lastMatch.guestTeam} className={classes.smallAvatar} />
                                            <Typography>{data.lastMatch.guestTeam ? data.lastMatch.guestTeam.name : 'Equipe visiteuse'}</Typography>
                                        </div>
                                        <Typography className={classes.gameScore}>Score: {getScoreLabel(data.lastMatch)}</Typography>
                                        <Typography>
                                            <Moment locale={i18next.language} format="ddd Do MMMM YYYY - HH:mm" date={data.lastMatch.gameDateTime} />
                                        </Typography>
                                        <ListItem
                                            button
                                            component={Link}
                                            to={`/home/calendar/${leagueID}/summary/${data.lastMatch.gameID}`}
                                        >
                                            <ListItemText primary="Voir le detail du match" />
                                        </ListItem>
                                    </>
                                    :
                                    <Typography className={classes.muted}>Aucun match joue.</Typography>
                            }
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper className={classes.pageSection}>
                            <Typography variant="h6" className={classes.sectionTitle}>Prochain match</Typography>
                            {
                                data.nextMatch ?
                                    <>
                                        <div className={classes.matchupRow}>
                                            <TeamAvatar team={data.nextMatch.homeTeam} className={classes.smallAvatar} />
                                            <Typography>{data.nextMatch.homeTeam ? data.nextMatch.homeTeam.name : 'Equipe locale'}</Typography>
                                            <Typography className={classes.versus}>vs</Typography>
                                            <TeamAvatar team={data.nextMatch.guestTeam} className={classes.smallAvatar} />
                                            <Typography>{data.nextMatch.guestTeam ? data.nextMatch.guestTeam.name : 'Equipe visiteuse'}</Typography>
                                        </div>
                                        <Typography className={classes.gameScore}>Score: {getScoreLabel(data.nextMatch)}</Typography>
                                        <Typography>
                                            <Moment locale={i18next.language} format="ddd Do MMMM YYYY - HH:mm" date={data.nextMatch.gameDateTime} />
                                        </Typography>
                                        <ListItem
                                            button
                                            component={Link}
                                            to={`/home/calendar/${leagueID}/summary/${data.nextMatch.gameID}`}
                                        >
                                            <ListItemText primary="Voir le detail du match" />
                                        </ListItem>
                                    </>
                                    :
                                    <Typography className={classes.muted}>Aucun prochain match planifie.</Typography>
                            }
                        </Paper>
                    </Grid>
                </Grid>

                <Paper className={classes.pageSection}>
                    <Typography variant="h6" className={classes.sectionTitle}>Fiche des 10 derniers matchs</Typography>
                    <List>
                        {
                            (data.lastTenMatches || []).length === 0 ?
                                <Typography className={classes.muted}>Aucun match disponible.</Typography>
                                :
                                (data.lastTenMatches || []).map((game, index) => (
                                    <React.Fragment key={game.gameID}>
                                        <ListItem
                                            button
                                            component={Link}
                                            to={`/home/calendar/${leagueID}/summary/${game.gameID}`}
                                        >
                                            <ListItemAvatar>
                                                <TeamAvatar team={game.homeTeam} className={classes.smallAvatar} />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={getMatchupLabel(game)}
                                                secondary={<Moment locale={i18next.language} format="ddd Do MMMM YYYY - HH:mm" date={game.gameDateTime} />}
                                            />
                                            <ListItemAvatar>
                                                <TeamAvatar team={game.guestTeam} className={classes.smallAvatar} />
                                            </ListItemAvatar>
                                            <Typography className={classes.gameScore}>{getScoreLabel(game)}</Typography>
                                        </ListItem>
                                        {index < data.lastTenMatches.length - 1 ? <Divider component="li" /> : null}
                                    </React.Fragment>
                                ))
                        }
                    </List>
                </Paper>
            </ScrollBody>
        </>
    );
};

export default TeamPage;
